import { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, ChevronDown, ChevronUp } from 'lucide-react';
import type { ResolutionTrendPoint } from '@/lib/api';

interface ResolutionTimePanelProps {
  avgWeek: number | null;
  avgMonth: number | null;
  avgAllTime: number | null;
  weekly: ResolutionTrendPoint[];
  monthly: ResolutionTrendPoint[];
}

/** Human-friendly resolution duration: minutes under 1h, hours under a day, else days. */
function formatHours(hours: number | null): string {
  if (hours == null) return 'N/A';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

function formatPeriod(iso: string, granularity: 'week' | 'month'): string {
  const d = new Date(iso);
  return granularity === 'month'
    ? d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function Stat({ label, value }: { label: string; value: number | null }) {
  return (
    <div>
      <div className="text-lg font-bold leading-none">{formatHours(value)}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export function ResolutionTimePanel({
  avgWeek,
  avgMonth,
  avgAllTime,
  weekly,
  monthly,
}: ResolutionTimePanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [granularity, setGranularity] = useState<'week' | 'month'>('month');

  const source = granularity === 'month' ? monthly : weekly;
  const chartData = source
    .filter((p) => p.avgHours != null)
    .map((p) => ({
      label: formatPeriod(p.periodStart, granularity),
      hours: p.avgHours as number,
      resolvedCount: p.resolvedCount,
    }));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">Resolution time</CardTitle>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? 'Hide trend' : 'View trend'}
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5 ml-1" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 ml-1" />
          )}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex gap-8">
          <Stat label="This week" value={avgWeek} />
          <Stat label="This month" value={avgMonth} />
          <Stat label="All time" value={avgAllTime} />
        </div>

        {expanded && (
          <div className="mt-4 border-t pt-4">
            <div className="mb-3 flex items-center gap-1">
              <Button
                variant={granularity === 'week' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setGranularity('week')}
              >
                Weekly
              </Button>
              <Button
                variant={granularity === 'month' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setGranularity('month')}
              >
                Monthly
              </Button>
            </div>

            {chartData.length === 0 ? (
              <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">
                No resolved tickets in this period yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    stroke="hsl(var(--border))"
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    stroke="hsl(var(--border))"
                    width={48}
                    label={{
                      value: 'hours',
                      angle: -90,
                      position: 'insideLeft',
                      style: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' },
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(value: number, _name, entry) => [
                      `${formatHours(value)} avg · ${entry.payload.resolvedCount} resolved`,
                      'Resolution',
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="hours"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
